import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import Select from "react-select";
import AxiosService from "../../../Utils/AxiosService";
import LocalStorageService from "../../../Auth/auth_service/AuthServiceStorage";
import { toast } from "react-toastify";
import { faEye, faDownload, faSpinner, faTrash, faFileAlt, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./uploadFile.css";

/* -------------------- SELECT STYLES -------------------- */
const greenStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "#fff",
    borderColor: state.isFocused ? "#6fcf9b" : "#9adbb5",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(111,207,155,0.15)" : "none",
    borderRadius: "8px",
    minHeight: "42px",
    "&:hover": { borderColor: "#6fcf9b" },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#c9eddc" : state.isFocused ? "#e6f6ee" : "#fff",
    color: "#245c3b",
  }),
};

export const UploadFiles = () => {
  const apiService = useMemo(() => new AxiosService(), []);
  const fileInputRef = useRef(null);

  const [country, setCountry] = useState({});
  const [projectId, setProjectId] = useState({});
  const [tradeNameArr, setTradeNameArr] = useState([]);
  const [isTradeNameLocked, setIsTradeNameLocked] = useState(false);
  const authDetails = JSON.parse(LocalStorageService.getAuthDetails());

  // labelId = curationPatentId — used for file view/download/delete and re-upload formData
  const [labelId, setLabelId] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [territory, setTerritory] = useState('');
  const [clientId, setClientId] = useState('');
  const [actualClientId, setActualClientId] = useState('');
  const [bilingualLanguages, setBilingualLanguages] = useState([]);
  const [files, setFiles] = useState([]);

  const [showTradeNameDropdown, setShowTradeNameDropdown] = useState(false);
  const [inputFiles, setInputFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [fileActionLoading, setFileActionLoading] = useState({});
  const [fileErrors, setFileErrors] = useState([]);

  // qcPatentIdForFetch = qcPatentId — used ONLY for fetchInputFiles API
  // Dropdown: item.id IS the qcPatentId (API returns { "121241": "ADDITION®" } → id=121241=qcPatentId)
  // After save: backend returns qcPatentId explicitly for both new and existing labels
  const [qcPatentIdForFetch, setQcPatentIdForFetch] = useState(null);
  const[userId,setUserId]=useState();

  const localStorageService = LocalStorageService.getService();

  const fetchInputFiles = useCallback((patentId) => {
    if (!patentId || patentId === "0") return;
    setLoadingFiles(true);
    apiService.fetchInputFiles(patentId)
      .then((res) => {
        setInputFiles(res.data || []);
        setLoadingFiles(false);
      })
      .catch(() => {
        setLoadingFiles(false);
        setInputFiles([]);
        setFileErrors([]);
      });
  }, [apiService]);

  // Trigger fetchInputFiles only via qcPatentIdForFetch — never via labelId (curationPatentId)
  useEffect(() => {
    if (qcPatentIdForFetch && !isUploading) {
      fetchInputFiles(qcPatentIdForFetch);
    }
  }, [qcPatentIdForFetch, fetchInputFiles, isUploading]);

  useEffect(() => {
    if (!clientId || clientId.value !== "22") setActualClientId(0);
  }, [clientId]);

  const filteredTradeNames = tradeNameArr.filter(item =>
    item.name.toLowerCase().includes(tradeName.toLowerCase())
  );

  const selectedLanguageIds = bilingualLanguages.map(item => item.value);
  const isViewableFile = (file) => file.toLowerCase().endsWith(".pdf");

  const viewFile = (filename) => {
    setFileActionLoading(prev => ({ ...prev, [filename]: "view" }));
    // labelId here is curationPatentId — correct for ManagerMode file operations
    apiService.viewFile(filename, qcPatentIdForFetch, authDetails.id)
      .then(res => {
        const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/pdf" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 500);
      })
      .catch(() => toast.error("Unable to view file"))
      .finally(() => setFileActionLoading(prev => { const n = { ...prev }; delete n[filename]; return n; }));
  };

  const downloadFile = (filename) => {
    setFileActionLoading(prev => ({ ...prev, [filename]: "download" }));
    // labelId here is curationPatentId — correct for ManagerMode file operations
    apiService.downloadFile(filename, qcPatentIdForFetch, authDetails.id)
      .then(res => {
        const blob = new Blob([res.data], { type: "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url; link.download = filename;
        document.body.appendChild(link); link.click();
        document.body.removeChild(link); URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Unable to download file"))
      .finally(() => setFileActionLoading(prev => { const n = { ...prev }; delete n[filename]; return n; }));
  };

  const deleteFile = (filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) return;
    setFileActionLoading(prev => ({ ...prev, [filename]: "delete" }));
    // labelId here is curationPatentId — correct for ManagerMode file operations
    apiService.deleteFile(filename, qcPatentIdForFetch, authDetails.id)
      .then(() => {
        toast.success("File deleted successfully");
        setInputFiles(prev => prev.filter(f => f !== filename));
      })
      .catch(() => toast.error("Unable to delete file"))
      .finally(() => setFileActionLoading(prev => { const n = { ...prev }; delete n[filename]; return n; }));
  };

  useEffect(() => {
    apiService.getCountryForSpecificUser(authDetails.id)
      .then((res) => setCountry(res?.data || {}))
      .catch(console.error);

    apiService.getAllClientsNameForSpecificUser(authDetails.id)
      .then((res) => setProjectId(res?.data || {}))
      .catch(console.error);
  }, [apiService, authDetails.id]);

  useEffect(() => {
    if (clientId && territory) {
      apiService.getLabelForParticularUserAndLanguage(authDetails.id, territory.value, Number(clientId.value))
        .then((res) => {
          const mapObject = res.data?.[0] || {};
          // API returns { "121241": "ADDITION®" } where key=qcPatentId, value=name
          const tradeOptions = Object.entries(mapObject).map(([id, name]) => ({ id, name }));
          setTradeNameArr(tradeOptions || []);
        }).catch(console.error);
    }
  }, [apiService, authDetails.id, clientId, territory]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setTradeName(value);
    setLabelId("0");
    setQcPatentIdForFetch(null);
    setIsTradeNameLocked(false);
    setInputFiles([]);
    setFileErrors([]);
  };

  const handleSelectTradeName = (item) => {
    setTradeName(item.name);
    setLabelId(item.id);
    // item.id from getLabelForParticularUserAndLanguage is qcPatentId
    setQcPatentIdForFetch(item.id);
    setIsTradeNameLocked(true);
    setShowTradeNameDropdown(false);
    // FIX: item.id is qcPatentId — correct for fetchInputFiles
    fetchInputFiles(item.id);
  };

  const clearTradeName = () => {
    setTradeName('');
    setLabelId('0');
    setQcPatentIdForFetch(null);
    setIsTradeNameLocked(false);
    setShowTradeNameDropdown(false);
    setInputFiles([]);
    setFileErrors([]);
  };

  useEffect(() => {
  const authVal = localStorageService.getAuthDetails();
  const id = JSON.parse(authVal)?.id;
  setUserId(id);
}, []);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    const MAX_FILE_SIZE = 15 * 1024 * 1024;
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ];

    const newErrors = [];
    const validFiles = [];

    selectedFiles.forEach((f) => {
      const isTooLarge = f.size > MAX_FILE_SIZE;
      const isInvalidType = !allowedTypes.includes(f.type) && !f.name.endsWith('.docx') && !f.name.endsWith('.doc');

      if (isTooLarge) {
        newErrors.push(`${f.name}: Exceeds 15MB limit`);
      } else if (isInvalidType) {
        newErrors.push(`${f.name}: Invalid file type (Only PDF/DOC/DOCX allowed)`);
      } else {
        validFiles.push(f);
      }
    });

    if (newErrors.length > 0) setFileErrors(prev => [...prev, ...newErrors]);

    if (validFiles.length > 0) {
      setFiles((prev) => {
        const existingNames = new Set(prev.map((file) => file.name));
        const filteredUnique = validFiles.filter((f) => !existingNames.has(f.name));
        return [...prev, ...filteredUnique];
      });
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (!clientId || !territory || !tradeName || files.length === 0) {
      toast.error("Please ensure Client, Territory, Trade Name, and Files are selected.");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setFileErrors([]);

    try {
      const formData = new FormData();
      // labelId here is curationPatentId (or "0" for new labels) — correct for backend
      formData.append("labelId", labelId || "0");
      formData.append("tradeName", tradeName);
      formData.append("territory", territory.value);
      formData.append("clientId", clientId.value);
      formData.append("userId", userId);

      if (clientId.value === "22") {
        formData.append("actualClientId", actualClientId.value);
      }
  
      selectedLanguageIds.forEach(lang =>
        formData.append("bilingualLanguages", lang)
      );
      files.forEach(file => formData.append("files", file));
      const res = await apiService.addLabelAndUploadInputFiles(
        formData,
        (percent) => setUploadProgress(percent)
      );

      const response = res.data;

      if (!response) {
        throw new Error("Empty response from server");
      }

      // FIX: Keep resLabelId (curationPatentId) and qcPatentId strictly separate
      // resLabelId = curationPatentId → for labelId state (re-upload, view, download, delete)
      // qcPatentId  = qcPatentId      → for qcPatentIdForFetch state (fetchInputFiles only)
      const { labelId: resLabelId, qcPatentId, fileUpload } = response;

      // Set curationPatentId for future re-uploads and file operations
      if (resLabelId) {
        setLabelId(resLabelId.toString());
        setIsTradeNameLocked(true);
      }

      // FIX: Set qcPatentId for fetchInputFiles — backend now always returns this
      // for both new labels (from addNewLabelToDigitalNow) and existing labels
      // (looked up from CurationAndQcMapping via findByCurationPatentId)
      // The useEffect on qcPatentIdForFetch will trigger fetchInputFiles automatically
      if (qcPatentId) {
        setQcPatentIdForFetch(qcPatentId.toString());
      }

      if (!fileUpload) {
        toast.success("Label created/updated successfully");
        setFiles([]);
        setFileErrors([]);
        return;
      }

      const { status, uploadedFiles = [], failedFiles = [] } = fileUpload;

      if (status === "SUCCESS") {
        if (uploadedFiles.length === 1) {
          toast.success(`File uploaded successfully`);
        } else {
          toast.success(`${uploadedFiles.length} files uploaded successfully`);
        }
        setFiles([]);
        setFileErrors([]);
      } else if (status === "PARTIAL_SUCCESS") {
        const successMsg = uploadedFiles.length > 0 ?
          `${uploadedFiles.length} file(s) uploaded successfully. ` : '';
        const failMsg = failedFiles.length > 0 ?
          `${failedFiles.length} file(s) failed:` : 'Some files failed';

        toast.warning(`${successMsg}${failMsg}`);

        if (failedFiles.length > 0) {
          setFileErrors(failedFiles.map((error) => `• ${error}`));
        }
        setFiles([]);
      } else { // FAILED
        toast.error("Failed to Upload File/Files");

        if (failedFiles.length > 0) {
          setFileErrors(failedFiles.map((error) => `• ${error}`));
        } else {
          setFileErrors(["Upload failed - no files were processed"]);
        }
      }

      // FIX: Do NOT call fetchInputFiles here directly — the useEffect on
      // qcPatentIdForFetch already handles this when qcPatentId is set above.
      // Calling it here too would cause a duplicate fetch with the wrong ID.

    } catch (error) {
      console.error("Upload error:", error);

      let errorMsg = "Upload failed due to server error";
      let detailedErrors = [];

      if (error.response?.data?.fileUpload?.failedFiles?.length > 0) {
        detailedErrors = error.response.data.fileUpload.failedFiles.map(
          err => `• ${err}`
        );
        errorMsg = "Upload failed";
      } else if (error.response?.status === 413) {
        errorMsg = "Upload rejected by server (size or file count limit)";
        detailedErrors = [errorMsg];
      } else if (error.response?.status === 400) {
        if (typeof error.response.data === 'string') {
          errorMsg = error.response.data;
          detailedErrors = [errorMsg];
        } else if (error.response.data?.message) {
          errorMsg = error.response.data.message;
          detailedErrors = [errorMsg];
        }
      } else if (error.response?.status === 401) {
        errorMsg = "Unauthorized - please login again";
        detailedErrors = [errorMsg];
      } else if (error.response?.data) {
        const responseData = error.response.data;

        if (responseData.fileUpload) {
          const { status, failedFiles = [] } = responseData.fileUpload;
          if (status === "FAILED" && failedFiles.length > 0) {
            detailedErrors = failedFiles.map(error => `• ${error}`);
            errorMsg = `${failedFiles.length} file(s) failed validation`;
          } else if (responseData.message) {
            errorMsg = responseData.message;
            detailedErrors = [errorMsg];
          }
        } else if (responseData.message) {
          errorMsg = responseData.message;
          detailedErrors = [errorMsg];
        } else if (typeof responseData === 'string') {
          errorMsg = responseData;
          detailedErrors = [errorMsg];
        }
      } else if (error.message) {
        errorMsg = error.message;
        detailedErrors = [errorMsg];
      }

      toast.error(errorMsg);
      setFileErrors(detailedErrors.length > 0 ? detailedErrors : [errorMsg]);

    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleReset = () => {
    setClientId(''); setActualClientId(''); setTerritory(''); setBilingualLanguages([]);
    setTradeName(''); setLabelId('0'); setQcPatentIdForFetch(null);
    setIsTradeNameLocked(false);
    setFiles([]); setUploadProgress(0); setInputFiles([]); setFileErrors([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
    toast.info("Form reset successfully");
  };

  return (
    <div className="container py-4">
      <div className="card border-0 shadow-sm" style={{ borderRadius: "12px" }}>
        <div className="card-body p-4">
          <h6 className="mb-4 fw-bold text-center" style={{ color: "#6fcf9b", fontSize: '1.1rem' }}>Upload Project Files</h6>

          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-medium small text-muted">CLIENT</label>
              <Select
                styles={greenStyles}
                options={Object.entries(projectId).map(([k, v]) => ({ value: k, label: v }))}
                value={clientId}
                onChange={(s) => {
                  setClientId(s);
                  setActualClientId(0);
                  setTradeName('');
                  setIsTradeNameLocked(false);
                  setBilingualLanguages([]);
                  setInputFiles([]);
                  setFileErrors([]);
                  setQcPatentIdForFetch(null);
                }}
                placeholder="Select Client"
                isClearable
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-medium small text-muted">TERRITORY</label>
              <Select
                styles={greenStyles}
                options={Object.entries(country).map(([k, v]) => ({ value: k, label: v }))}
                value={territory}
                onChange={(selected) => {
                  setTerritory(selected);
                  setTradeName('');
                  setIsTradeNameLocked(false);
                  setBilingualLanguages([]);
                  setInputFiles([]);
                  setFileErrors([]);
                  setQcPatentIdForFetch(null);
                }}
                placeholder="Select Territory"
                isClearable
              />
            </div>

            {clientId?.value === "22" && (
              <div className="col-md-6">
                <label className="form-label fw-medium small">ACTUAL CLIENT *</label>
                <Select
                  styles={greenStyles}
                  options={Object.entries(projectId).map(([k, v]) => ({ value: k, label: v }))}
                  value={actualClientId}
                  onChange={setActualClientId}
                  isClearable
                />
              </div>
            )}

            {(territory && clientId) && (
              <div className="col-md-6">
                <label className="form-label fw-medium small text-muted">TRADE NAME</label>
                <div className="position-relative">
                  <input
                    type="text"
                    value={tradeName}
                    disabled={isTradeNameLocked}
                    onChange={(e) => {
                      handleInputChange(e);
                      setShowTradeNameDropdown(true);
                    }}
                    onFocus={() => setShowTradeNameDropdown(true)}
                    onBlur={() => setTimeout(() => setShowTradeNameDropdown(false), 150)}
                    placeholder="Type or Select a Trade Name"
                    className="form-control"
                    style={{ minHeight: "38px", paddingRight: "32px", borderRadius: "8px", border: "1px solid #9adbb5" }}
                  />
                  {tradeName && (
                    <button
                      type="button"
                      className="position-absolute top-50 end-0 translate-middle-y me-2 btn btn-sm btn-link text-muted"
                      onMouseDown={(e) => { e.preventDefault(); clearTradeName(); }}
                    >✕</button>
                  )}
                  {showTradeNameDropdown && filteredTradeNames.length > 0 && (
                    <ul className="list-group position-absolute w-100 shadow z-3 mt-1" style={{ maxHeight: "180px", overflowY: "auto" }}>
                      {filteredTradeNames.map(item => (
                        <li key={item.id} className="list-group-item list-group-item-action" onMouseDown={() => handleSelectTradeName(item)}>
                          {item.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {(territory && clientId) && (
              <div className="col-md-6">
                <label className="form-label fw-medium small text-muted">BILINGUAL LANGUAGES</label>
                <Select
                  styles={greenStyles}
                  options={Object.entries(country).map(([k, v]) => ({ value: k, label: v }))}
                  value={bilingualLanguages}
                  onChange={setBilingualLanguages}
                  isMulti
                  placeholder="Select Languages"
                />
              </div>
            )}

            <div className="col-md-12 mt-3">
              <label className="form-label fw-medium small text-muted">UPLOAD INPUT FILES</label>
              <input
                ref={fileInputRef}
                type="file"
                className="form-control"
                style={{ borderRadius: "8px", border: "1px solid #9adbb5" }}
                multiple
                accept=".doc,.docx,.pdf"
                onChange={handleFileChange}
              />

              {fileErrors.length > 0 && (
                <div className="alert alert-danger mt-3 py-3 px-3 border-0 shadow-sm d-flex align-items-start" style={{ borderRadius: "10px", backgroundColor: "#fff5f5", color: "#c53030" }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-3 mt-1" />
                  <div className="flex-grow-1">
                    <strong className="d-block mb-1" style={{ fontSize: "14px" }}>Rejection Details:</strong>
                    <ul className="mb-0 ps-3" style={{ fontSize: "13px" }}>
                      {fileErrors.map((err, idx) => (
                        <li key={idx} className="mb-1">{err}</li>
                      ))}
                    </ul>
                  </div>
                  <button type="button" className="btn-close" style={{ fontSize: "10px" }} onClick={() => setFileErrors([])}></button>
                </div>
              )}

              <div className="d-flex justify-content-between mt-2">
                <small className="text-muted">Max 15MB per file, Total Upload Size (max 50MB). Formats: PDF, DOC, DOCX</small>
                {files.length > 0 && (
                  <button type="button" className="btn btn-link btn-sm text-decoration-underline p-0 text-success" onClick={() => { setFiles([]); setFileErrors([]); }}>
                    Clear selection
                  </button>
                )}
              </div>
            </div>
          </div>

          {(files.length > 0 || isUploading) && (
            <div className="mt-4 p-3 rounded border-start border-4 border-success shadow-sm" style={{ backgroundColor: "#f8fdfa" }}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-bold" style={{ fontSize: '0.85rem', color: isUploading && uploadProgress === 100 ? "#007bff" : "#245c3b" }}>
                  {isUploading ? (
                    uploadProgress < 100 ?
                      <><FontAwesomeIcon icon={faSpinner} spin className="me-2" /> Uploading to Server...</> :
                      <><FontAwesomeIcon icon={faSpinner} spin className="me-2 text-primary" /> Verifying File Contents...</>
                  ) : 'Files Ready for Upload'}
                </span>
                <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{uploadProgress}%</span>
              </div>

              <div className="progress mb-3" style={{ height: "8px", borderRadius: "10px", backgroundColor: "#e9ecef" }}>
                <div
                  className={`progress-bar progress-bar-striped progress-bar-animated ${uploadProgress === 100 ? 'bg-primary' : 'bg-success'}`}
                  role="progressbar"
                  style={{ width: `${uploadProgress}%`, transition: "width 0.4s ease" }}
                ></div>
              </div>

              <div className="px-1" style={{ maxHeight: "100px", overflowY: "auto" }}>
                {files.map((file, idx) => (
                  <div key={idx} className="d-flex align-items-center mb-1 py-1 border-bottom border-light" style={{ fontSize: '0.8rem' }}>
                    <FontAwesomeIcon icon={faFileAlt} className="me-2 text-muted" />
                    <span className="text-truncate text-muted" style={{ maxWidth: "80%" }}>{file.name}</span>
                    <span className="ms-auto badge bg-light text-dark" style={{ fontSize: '0.65rem' }}>
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="row g-3 mt-4">
            <div className="col-6">
              <button
                className="btn w-100 py-2 fw-bold"
                onClick={handleSubmit}
                disabled={isUploading || files.length === 0}
                style={{
                  backgroundColor: isUploading || files.length === 0 ? "#ccc" : "var(--dashboard-green)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px"
                }}
              >
                {isUploading ? <><FontAwesomeIcon icon={faSpinner} spin className="me-2" /> Processing...</> : "Upload Files"}
              </button>
            </div>
            <div className="col-6">
              <button
                className="btn w-100 py-2 text-white"
                onClick={handleReset}
                style={{ backgroundColor: "var(--dashboard-gray)", borderRadius: "8px", border: "none" }}
              >
                Reset Form
              </button>
            </div>
          </div>
        </div>
      </div>

      {(territory && tradeName && clientId) && (
        <div className="mt-5 animate__animated animate__fadeIn">
          <div className="d-flex align-items-center mb-3">
            <div style={{ width: '4px', height: '20px', backgroundColor: '#245c3b', marginRight: '10px', borderRadius: '2px' }}></div>
            <h6 className="fw-bold mb-0">Total Files ({inputFiles.length} files)</h6>
          </div>
          <div className="card border-0 shadow-sm" style={{ borderRadius: "12px" }}>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-4 py-3 border-0">Filename</th>
                    <th className="text-end pe-4 border-0">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingFiles ? (
                    <tr><td colSpan="2" className="text-center py-5"><FontAwesomeIcon icon={faSpinner} spin size="lg" className="text-success" /></td></tr>
                  ) : inputFiles.length === 0 ? (
                    <tr><td colSpan="2" className="text-center py-5 text-muted">No files found for this Trade Name.</td></tr>
                  ) : (
                    inputFiles.map((file, index) => (
                      <tr key={index}>
                        <td className="ps-4">
                          <FontAwesomeIcon icon={faFileAlt} className="me-2 text-muted" />
                          <span className="fw-medium">{file}</span>
                        </td>
                        <td className="text-end pe-4">
                          <div className="d-inline-flex gap-2">
                            {isViewableFile(file) && (
                              <button className="btn btn-sm btn-outline-secondary" title="View" onClick={() => viewFile(file)}>
                                {fileActionLoading[file] === "view" ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faEye} />}
                              </button>
                            )}
                            <button className="btn btn-sm btn-outline-success" title="Download" onClick={() => downloadFile(file)}>
                              {fileActionLoading[file] === "download" ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faDownload} />}
                            </button>
                            <button className="btn btn-sm btn-outline-danger" title="Delete" onClick={() => deleteFile(file)}>
                              {fileActionLoading[file] === "delete" ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faTrash} />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};